import React, { useCallback, useEffect, useRef, useState } from "react";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import { makeStyles, tokens, FluentProvider, webDarkTheme, webLightTheme } from "@fluentui/react-components";
import { Logger } from "../../services/Logger";
import { EmailApiService } from "../../services/NewformaApi/EmailApiService";
import { MsGraphApiService } from "../../services/MsGraphApiService";
import { AnalyticsManager } from "../../services/AnalyticsManager";
import { AppPage } from "../shared/navigationHeader/NavigationHeaderComponent";
import { MailboxItem, OfficeWrapper } from "../../services/OfficeWrapper";
import { NewformaToastIntent } from "../_base/Toast";
import { ProjectPickerWithLoader } from "../shared/projectPicker/ProjectPickerWithLoader";
import { SmartFilingManagerFluent9 } from "../../services/SmartFiling/SmartFilingManagerFluent9";
import { InvalidateCacheService } from "../../services/NewformaApi/InvalidateCacheService";
import { IProjectsService } from "../../services/NewformaApi/IProjectsService";
import { IProjectData } from "../../models/shared/IProjectData";
import { ProjectsCacheKeys } from "../../models/StorageKeys";
import { NrnServiceWrapper } from "../../services/NrnServiceWrapper";
import { NumberEntry } from "../../models/projects/ProjectSettingsResponse";
import { NewformaInput } from "../_base/NewformaInput";
import { ExpiredSessionError } from "../../models/ExpiredSessionError";

const useStyles = makeStyles({
    root: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
        padding: `4px ${tokens.spacingHorizontalXXL} 0 ${tokens.spacingHorizontalXXL}`,
        gap: tokens.spacingVerticalL,
        overflowY: "auto",
        userSelect: "none",
        backgroundColor: tokens.colorNeutralBackground1,
    },
    footer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "stretch",
        flexShrink: 0,
        position: "sticky",
        bottom: 0,
        zIndex: 1,
        paddingTop: 0,
        paddingLeft: "24px",
        paddingRight: "24px",
        marginLeft: "-20px",
        marginRight: "-20px",
        marginTop: 0,
        backgroundColor: tokens.colorNeutralBackground1,
    },
});

interface RfiFluent9Props extends LocalizeContextProps {
    logger: Logger;
    onExpiredSession: () => void;
    onShowToast: (message: string | null, type: NewformaToastIntent) => void;
    onSetNavigationPage: (page: AppPage) => void;
    mailboxItem: MailboxItem | null;
    emailApiService: EmailApiService;
    msGraphApiService: MsGraphApiService;
    analyticsManager: AnalyticsManager;
    smartFilingManager: SmartFilingManagerFluent9;
    invalidateCacheService: InvalidateCacheService;
    projectsService: IProjectsService;
    officeWrapper: OfficeWrapper;
    nrnServiceWrapper: NrnServiceWrapper;
    theme: string;
    culture: string;
}

const RfiFluent9Inner: React.FC<RfiFluent9Props> = (props) => {
    const styles = useStyles();
    const isDarkTheme = props.theme === "dark";

    const [selectedProject, setSelectedProject] = useState<IProjectData | undefined>(undefined);
    const [number, setNumber] = useState<string>("");
    const [numberEntryMode, setNumberEntryMode] = useState<NumberEntry | null>(null);
    const [isCloudProject, setIsCloudProject] = useState<boolean>(true);

    // mirrors legacy RfiComponent.onProjectSelected()/getProjectSettings(): isCloudProject is known
    // synchronously from the nrn, while numberEntryMode requires an NPC project settings lookup
    useEffect(() => {
        if (!selectedProject) {
            setIsCloudProject(true);
            setNumberEntryMode(null);
            return;
        }

        setIsCloudProject(props.nrnServiceWrapper.isCloudProject(selectedProject.nrn));

        let isCancelled = false;
        (async () => {
            try {
                const projectSettings = await props.projectsService.getProjectSettings(selectedProject.nrn);
                if (!isCancelled) {
                    setNumberEntryMode(projectSettings?.rfis.numberEntryMode ?? null);
                }
            } catch (error) {
                if (isCancelled) {
                    return;
                }
                if (ExpiredSessionError.isInstanceOf(error)) {
                    props.onExpiredSession();
                    return;
                }
                props.logger.error("RfiFluent9Component - error loading project settings", error);
                setNumberEntryMode(null);
            }
        })();

        return () => {
            isCancelled = true;
        };
    }, [selectedProject, props.nrnServiceWrapper, props.projectsService, props.logger, props.onExpiredSession]);
    const [subject, setSubject] = useState<string>("");
    const [isLoadingProjects, setIsLoadingProjects] = useState<boolean>(false);
    const [isFiling, setIsFiling] = useState<boolean>(false);
    const prevMailboxItemRef = useRef<MailboxItem | null>(null);

    // mirrors legacy RfiComponent.populateDefaults(): (re)populate subject from the current email
    // both on mount and whenever the selected mailbox item changes
    useEffect(() => {
        const prevMailboxItem = prevMailboxItemRef.current;
        prevMailboxItemRef.current = props.mailboxItem;

        if (props.mailboxItem && prevMailboxItem !== props.mailboxItem) {
            setSubject((props.mailboxItem.subject ?? "").trim());
        }
    }, [props.mailboxItem]);

    const getProjects = useCallback(() => props.projectsService.getProjectsSupportingRfis(), [props.projectsService]);

    const isNumberManualEntry = !isCloudProject && numberEntryMode === NumberEntry.manual;

    return (
        <FluentProvider theme={isDarkTheme ? webDarkTheme : webLightTheme} style={{ display: "contents" }}>
            <div className={styles.root}>
                <ProjectPickerWithLoader
                    getProjects={getProjects}
                    cacheKey={ProjectsCacheKeys.rfisCacheName}
                    smartFilingManager={props.smartFilingManager}
                    invalidateCacheService={props.invalidateCacheService}
                    logger={props.logger}
                    officeWrapper={props.officeWrapper}
                    selectedProject={selectedProject}
                    onProjectChange={setSelectedProject}
                    onLoadingChange={setIsLoadingProjects}
                />
                <NewformaInput
                    label={props.translate("RFI.SUBJECT_LABEL") as string}
                    required={true}
                    id="rfi-subject"
                    data-testid="rfi-subject-input"
                    fieldDataTestId="rfi-subject-field"
                    value={subject}
                    onChange={(_e, data) => setSubject(data.value)}
                    disabled={isFiling || isLoadingProjects}
                    maxLength={256}
                    autoComplete="off"
                    placeholder={props.translate("RFI.SUBJECT_PLACEHOLDER") as string}
                />
                <NewformaInput
                    label={props.translate("RFI.NUMBER_LABEL") as string}
                    required={isNumberManualEntry}
                    id="rfi-number"
                    data-testid="rfi-number-input"
                    fieldDataTestId="rfi-number-field"
                    value={number}
                    onChange={(_e, data) => setNumber(data.value)}
                    disabled={isFiling || isLoadingProjects}
                    maxLength={256}
                    autoComplete="off"
                    placeholder={
                        isNumberManualEntry
                            ? (props.translate("RFI.NUMBER_MANUAL_PLACEHOLDER") as string)
                            : (props.translate("RFI.NUMBER_PLACEHOLDER") as string)
                    }
                />
                {/* Footer Section */}
                <div className={styles.footer} />
            </div>
        </FluentProvider>
    );
};

export default withLocalize(RfiFluent9Inner);
